import React, { useState, useEffect } from 'react';
import {
  GitBranch,
  Plus,
  Save,
  Trash2,
  Play,
  Copy,
  Edit3,
  ChevronRight,
  MessageSquare,
  Zap,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { conversationFlowAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const ConversationFlowPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Default flow structure
  const defaultFlow = {
    name: 'New Flow',
    description: '',
    enabled: true,
    priority: 0,
    conditions: [],
    actions: []
  };

  // Condition types with descriptions
  const conditionTypes = [
    {
      value: 'contains',
      label: 'Contains keyword',
      description: 'Partial match, case-insensitive. Example: "Brian" will match "transfer to Brian Southers"'
    },
    {
      value: 'exact',
      label: 'Exact match',
      description: 'Must match completely (case-insensitive). Example: "help" only matches "help", not "I need help"'
    },
    {
      value: 'starts_with',
      label: 'Starts with',
      description: 'Input starts with value. Example: "what" matches "what is your name?"'
    },
    {
      value: 'ends_with',
      label: 'Ends with',
      description: 'Input ends with value. Example: "hours?" matches "what are your hours?"'
    },
    {
      value: 'regex',
      label: 'Regex pattern',
      description: 'Advanced: matches a regular expression pattern'
    },
    {
      value: 'intent',
      label: 'Intent detection',
      description: 'Detected conversation intent. Examples: "transfer", "complaint", "pricing"'
    },
    {
      value: 'sentiment',
      label: 'Sentiment',
      description: 'Detected sentiment matches value. Options: "positive", "negative", "neutral"'
    },
    {
      value: 'time_of_day',
      label: 'Time of day',
      description: 'Current time is within range. Format: "09:00-17:00"'
    },
    {
      value: 'day_of_week',
      label: 'Day of week',
      description: 'Current day matches. Format: "monday,tuesday,wednesday"'
    }
  ];

  // Action types with descriptions
  const actionTypes = [
    {
      value: 'respond',
      label: 'Send response',
      description: 'AI will speak this response to the caller immediately'
    },
    {
      value: 'transfer',
      label: 'Transfer call',
      description: 'Transfer the call to the specified phone number'
    },
    {
      value: 'schedule',
      label: 'Schedule callback',
      description: 'AI will schedule a callback with the caller'
    },
    {
      value: 'email',
      label: 'Send email',
      description: 'Send an email notification automatically. AI will NOT prompt for a message - the email is sent immediately'
    },
    {
      value: 'take_message',
      label: 'Take message',
      description: 'AI will prompt the caller to leave a message, then send it to the specified email address'
    },
    {
      value: 'webhook',
      label: 'Call webhook',
      description: 'Send an HTTP request to an external URL for integrations'
    },
    {
      value: 'tag',
      label: 'Add tag',
      description: 'Add a tag to this conversation for categorization'
    },
    {
      value: 'escalate',
      label: 'Escalate to human',
      description: 'Route the call to a human agent'
    }
  ];

  useEffect(() => {
    loadFlows();
    loadTemplates();
  }, []);

  const loadFlows = async (preserveFlowId = null) => {
    try {
      setLoading(true);
      const response = await conversationFlowAPI.getFlows();
      const flowData = response.data?.data || response.data || [];

      // Transform backend data to frontend format
      const transformedFlows = flowData.map(flow => ({
        id: flow.id,
        name: flow.name,
        description: flow.description || '',
        enabled: flow.isActive !== undefined ? flow.isActive : true,
        priority: flow.priority || 0,
        conditions: flow.flowData?.conditions || flow.conditions || [],
        actions: flow.flowData?.actions || flow.actions || []
      }));

      setFlows(transformedFlows);

      // Preserve selected flow if ID provided, otherwise select first
      if (preserveFlowId) {
        const flowToSelect = transformedFlows.find(f => f.id === preserveFlowId);
        if (flowToSelect) {
          setSelectedFlow(flowToSelect);
        } else if (transformedFlows.length > 0) {
          setSelectedFlow(transformedFlows[0]);
        }
      } else if (transformedFlows.length > 0) {
        setSelectedFlow(transformedFlows[0]);
      }
    } catch (error) {
      console.error('Failed to load flows:', error);
      // Use default flows for demo
      const demoFlows = [
        {
          id: 1,
          name: 'Business Hours Check',
          description: 'Respond based on business hours',
          enabled: true,
          priority: 1,
          conditions: [
            { type: 'time_of_day', operator: 'between', value: '09:00-17:00' }
          ],
          actions: [
            { type: 'respond', value: 'Thank you for calling! How can I help you today?' }
          ]
        },
        {
          id: 2,
          name: 'After Hours Response',
          description: 'Handle calls outside business hours',
          enabled: true,
          priority: 2,
          conditions: [
            { type: 'time_of_day', operator: 'not_between', value: '09:00-17:00' }
          ],
          actions: [
            { type: 'respond', value: 'Thank you for calling. Our business hours are 9 AM to 5 PM. Would you like to leave a message or schedule a callback?' },
            { type: 'tag', value: 'after-hours' }
          ]
        },
        {
          id: 3,
          name: 'Appointment Booking',
          description: 'Handle appointment requests',
          enabled: true,
          priority: 3,
          conditions: [
            { type: 'contains', value: 'appointment' },
            { type: 'contains', value: 'schedule', operator: 'OR' },
            { type: 'contains', value: 'book', operator: 'OR' }
          ],
          actions: [
            { type: 'respond', value: "I'd be happy to help you schedule an appointment. What date and time work best for you?" },
            { type: 'tag', value: 'appointment-request' }
          ]
        }
      ];
      setFlows(demoFlows);
      setSelectedFlow(demoFlows[0]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await conversationFlowAPI.getFlowTemplates();
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Use demo templates
      setTemplates([
        {
          id: 1,
          name: 'Customer Service Basic',
          description: 'Common customer service responses',
          flows: [
            { name: 'Greeting', conditions: [{ type: 'starts_with', value: 'hello' }], actions: [{ type: 'respond', value: 'Hello! Welcome to our business. How can I assist you today?' }] },
            { name: 'Pricing', conditions: [{ type: 'contains', value: 'price' }], actions: [{ type: 'respond', value: 'I can help you with pricing information. What specific product or service are you interested in?' }] }
          ]
        },
        {
          id: 2,
          name: 'E-commerce Support',
          description: 'Handle common e-commerce queries',
          flows: [
            { name: 'Order Status', conditions: [{ type: 'contains', value: 'order' }], actions: [{ type: 'respond', value: 'I can help you check your order status. Please provide your order number.' }] },
            { name: 'Returns', conditions: [{ type: 'contains', value: 'return' }], actions: [{ type: 'respond', value: 'Our return policy allows returns within 30 days. Would you like to initiate a return?' }] }
          ]
        }
      ]);
    }
  };

  const createNewFlow = () => {
    const newFlow = {
      ...defaultFlow,
      id: Date.now(),
      name: `Flow ${flows.length + 1}`,
      conditions: [{ type: 'contains', value: '', operator: 'AND' }],
      actions: [{ type: 'respond', value: '' }]
    };
    setFlows([...flows, newFlow]);
    setSelectedFlow(newFlow);
  };

  const updateFlow = (flowId, updates) => {
    setFlows(flows.map(f => f.id === flowId ? { ...f, ...updates } : f));
    if (selectedFlow?.id === flowId) {
      setSelectedFlow({ ...selectedFlow, ...updates });
    }
  };

  const deleteFlow = (flowId) => {
    setFlows(flows.filter(f => f.id !== flowId));
    if (selectedFlow?.id === flowId) {
      setSelectedFlow(flows[0] || null);
    }
  };

  const addCondition = () => {
    if (!selectedFlow) {return;}
    const newCondition = { type: 'contains', value: '', operator: 'AND' };
    updateFlow(selectedFlow.id, {
      conditions: [...selectedFlow.conditions, newCondition]
    });
  };

  const updateCondition = (index, updates) => {
    if (!selectedFlow) {return;}
    const newConditions = [...selectedFlow.conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    updateFlow(selectedFlow.id, { conditions: newConditions });
  };

  const removeCondition = (index) => {
    if (!selectedFlow) {return;}
    const newConditions = selectedFlow.conditions.filter((_, i) => i !== index);
    updateFlow(selectedFlow.id, { conditions: newConditions });
  };

  const addAction = () => {
    if (!selectedFlow) {return;}
    const newAction = { type: 'respond', value: '' };
    updateFlow(selectedFlow.id, {
      actions: [...selectedFlow.actions, newAction]
    });
  };

  const updateAction = (index, updates) => {
    if (!selectedFlow) {return;}
    const newActions = [...selectedFlow.actions];
    newActions[index] = { ...newActions[index], ...updates };
    updateFlow(selectedFlow.id, { actions: newActions });
  };

  const removeAction = (index) => {
    if (!selectedFlow) {return;}
    const newActions = selectedFlow.actions.filter((_, i) => i !== index);
    updateFlow(selectedFlow.id, { actions: newActions });
  };

  const saveFlows = async () => {
    try {
      setSaving(true);
      // Save all flows
      for (const flow of flows) {
        // Transform flow data to match backend expectations
        const flowData = {
          name: flow.name,
          description: flow.description || '',
          trigger: 'keyword', // Default trigger type
          flowData: {
            conditions: flow.conditions || [],
            actions: flow.actions || []
          },
          isActive: flow.enabled !== undefined ? flow.enabled : true,
          priority: flow.priority || 0
        };

        if (flow.id > 1000000) {
          // New flow (temporary ID)
          await conversationFlowAPI.createFlow(flowData);
        } else {
          await conversationFlowAPI.updateFlow(flow.id, flowData);
        }
      }
      toast.success('Conversation flows saved successfully');
      // Reload flows and preserve the currently selected flow
      await loadFlows(selectedFlow?.id);
    } catch (error) {
      console.error('Save flows error:', error);
      toast.error('Failed to save conversation flows');
    } finally {
      setSaving(false);
    }
  };

  const testFlow = async () => {
    if (!selectedFlow || !testInput) {
      toast.error('Please enter test input');
      return;
    }

    try {
      const response = await conversationFlowAPI.testFlow(selectedFlow.id, testInput);
      // Backend returns { success: true, data: result }
      const result = response.data?.data || response.data;
      setTestResult(result);
    } catch (error) {
      console.error('Test flow error:', error);
      // Simulate test for demo/fallback
      const matchesCondition = selectedFlow.conditions.some(cond => {
        if (cond.type === 'contains') {
          return testInput.toLowerCase().includes(cond.value.toLowerCase());
        }
        if (cond.type === 'starts_with') {
          return testInput.toLowerCase().startsWith(cond.value.toLowerCase());
        }
        if (cond.type === 'exact') {
          return testInput.toLowerCase() === cond.value.toLowerCase();
        }
        return false;
      });

      setTestResult({
        matched: matchesCondition,
        response: matchesCondition ? selectedFlow.actions[0]?.value : 'No matching flow found',
        flow: matchesCondition ? selectedFlow.name : null
      });
    }
  };

  const applyTemplate = (template) => {
    const newFlows = template.flows.map((flow, index) => ({
      ...defaultFlow,
      ...flow,
      id: Date.now() + index,
      enabled: true,
      priority: flows.length + index + 1
    }));
    setFlows([...flows, ...newFlows]);
    toast.success(`Applied ${template.name} template`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading conversation flows..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conversation Flows</h1>
          <p className="text-gray-600 mt-1">
            Create if/then rules to automate responses and actions
          </p>
        </div>
        <button
          onClick={saveFlows}
          disabled={saving}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save All Flows'}</span>
        </button>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Flow List */}
        <div className="col-span-1 bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold">Flows</h2>
            <button
              onClick={createNewFlow}
              className="text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-2">
            {flows.map(flow => (
              <div
                key={flow.id}
                onClick={() => setSelectedFlow(flow)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedFlow?.id === flow.id
                    ? 'bg-blue-50 border-blue-200 border'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{flow.name}</p>
                    <p className="text-xs text-gray-500">
                      {flow.conditions.length} conditions, {flow.actions.length} actions
                    </p>
                  </div>
                  {flow.enabled ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Templates */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-medium text-sm mb-3">Templates</h3>
            <div className="space-y-2">
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => applyTemplate(template)}
                  className="w-full text-left p-2 text-sm rounded hover:bg-gray-50"
                >
                  <p className="font-medium">{template.name}</p>
                  <p className="text-xs text-gray-500">{template.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Flow Editor */}
        {selectedFlow && (
          <div className="col-span-3 bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <input
                  type="text"
                  value={selectedFlow.name}
                  onChange={(e) => updateFlow(selectedFlow.id, { name: e.target.value })}
                  className="text-lg font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                />
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedFlow.enabled}
                      onChange={(e) => updateFlow(selectedFlow.id, { enabled: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Enabled</span>
                  </label>
                  <button
                    onClick={() => deleteFlow(selectedFlow.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <textarea
                placeholder="Description (optional)"
                value={selectedFlow.description}
                onChange={(e) => updateFlow(selectedFlow.id, { description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {/* IF Conditions */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                  IF (Conditions)
                </h3>
                <button
                  onClick={addCondition}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Add Condition
                </button>
              </div>

              <div className="space-y-3">
                {selectedFlow.conditions.map((condition, index) => {
                  const selectedType = conditionTypes.find(t => t.value === condition.type);
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center space-x-2">
                        {index > 0 && (
                          <select
                            value={condition.operator || 'AND'}
                            onChange={(e) => updateCondition(index, { operator: e.target.value })}
                            className="px-2 py-1 text-xs border border-gray-300 rounded"
                          >
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                          </select>
                        )}

                        <select
                          value={condition.type}
                          onChange={(e) => updateCondition(index, { type: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          {conditionTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>

                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, { value: e.target.value })}
                          placeholder="Value"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        />

                        <button
                          onClick={() => removeCondition(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {selectedType?.description && (
                        <p className="text-xs text-gray-500 ml-16 italic">
                          {selectedType.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* THEN Actions */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center">
                  <ChevronRight className="w-4 h-4 mr-2 text-green-500" />
                  THEN (Actions)
                </h3>
                <button
                  onClick={addAction}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Add Action
                </button>
              </div>

              <div className="space-y-3">
                {selectedFlow.actions.map((action, index) => {
                  const selectedType = actionTypes.find(t => t.value === action.type);
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{index + 1}.</span>

                        <select
                          value={action.type}
                          onChange={(e) => updateAction(index, { type: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          {actionTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>

                        {action.type === 'respond' && (
                          <textarea
                            value={action.value}
                            onChange={(e) => updateAction(index, { value: e.target.value })}
                            placeholder="Response message"
                            rows={2}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}

                        {action.type === 'transfer' && (
                          <input
                            type="text"
                            value={action.value}
                            onChange={(e) => updateAction(index, { value: e.target.value })}
                            placeholder="Phone number"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}

                        {action.type === 'tag' && (
                          <input
                            type="text"
                            value={action.value}
                            onChange={(e) => updateAction(index, { value: e.target.value })}
                            placeholder="Tag name"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}

                        {action.type === 'take_message' && (
                          <input
                            type="text"
                            value={action.value}
                            onChange={(e) => updateAction(index, { value: e.target.value })}
                            placeholder="Where to send message (email)"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}

                        {(action.type === 'email' || action.type === 'webhook') && (
                          <input
                            type="text"
                            value={action.value}
                            onChange={(e) => updateAction(index, { value: e.target.value })}
                            placeholder={action.type === 'webhook' ? 'URL' : 'Email address'}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}

                        <button
                          onClick={() => removeAction(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {selectedType?.description && (
                        <p className="text-xs text-gray-500 ml-8 italic">
                          {selectedType.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Test Flow */}
            <div className="border-t pt-6">
              <h3 className="font-medium mb-3 flex items-center">
                <Play className="w-4 h-4 mr-2" />
                Test This Flow
              </h3>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="Enter test input or JSON context..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <button
                  onClick={testFlow}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Test
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Tip: Provide structured JSON for advanced checks, e.g. {`{"text":"Schedule a visit","intent":"appointment","sentiment":"positive","timeOfDay":"10:30","dayOfWeek":"Tuesday"}`}.
              </p>

              {testResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  testResult.matched ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                }`}>
                  <p className="text-sm font-medium mb-1">
                    {testResult.matched ? '✅ Flow matched!' : '❌ No match'}
                  </p>
                  {testResult.response && (
                    <p className="text-sm text-gray-700">
                      <strong>Response:</strong> {testResult.response}
                    </p>
                  )}
                  {testResult.flow && (
                    <p className="text-sm text-gray-500">
                      <strong>Flow:</strong> {testResult.flow}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationFlowPage;
